import { useState } from "react";
import { Link } from "react-router-dom";
import type { MangaItem } from "@/mocks/comics";
import { getCategoryBySlug } from "@/mocks/categories";
import { formatCount } from "@/utils/format";

interface InfoPanelProps {
  comic: MangaItem;
  onStartReading: () => void;
}

export default function InfoPanel({ comic, onStartReading }: InfoPanelProps) {
  const [liked, setLiked] = useState(false);
  const [collected, setCollected] = useState(false);

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-4 md:gap-8">
      <div className="mx-auto w-52 shrink-0 md:mx-0 md:w-full">
        <div className="relative overflow-hidden rounded-lg border border-background-200/70">
          <img
            src={comic.cover}
            alt={`${comic.title} 漫画封面`}
            title={`${comic.title} 漫画在线免费阅读`}
            className="aspect-[5/7] w-full object-cover object-top"
          />
          <span
            className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-xs font-medium text-background-50 ${
              comic.status === "已完结" ? "bg-accent-500" : "bg-primary-500"
            }`}
          >
            {comic.status}
          </span>
        </div>
      </div>

      <div className="md:col-span-3">
        <div className="flex flex-wrap items-center gap-2">
          {comic.categorySlugs.map((slug) => {
            const cat = getCategoryBySlug(slug);
            if (!cat) return null;
            return (
              <Link
                key={slug}
                to={`/category/${slug}`}
                className="chip-tag hover:bg-secondary-200 transition-colors cursor-pointer"
              >
                {cat.name}漫画
              </Link>
            );
          })}
          {comic.status === "已完结" && (
            <span className="chip-tag !bg-accent-100 !text-accent-900">
              <i className="ri-check-double-line" aria-hidden="true" />
              大结局已上线
            </span>
          )}
        </div>

        <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
          {comic.title}
        </h1>
        <p className="mt-1.5 text-xs text-foreground-600 md:text-sm">原名：{comic.originalTitle} · 作者：{comic.author}</p>

        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-700">
          <span className="flex items-center gap-1.5">
            <i className="ri-star-fill text-accent-500" aria-hidden="true" />
            <strong className="text-foreground-950">{comic.rating.toFixed(1)}</strong> 分
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-line" aria-hidden="true" />
            人气 {comic.views}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-bookmark-line" aria-hidden="true" />
            共 {comic.chapters.length} 话
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line" aria-hidden="true" />
            更新于 {comic.updateDate}
          </span>
        </div>

        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-[15px]">
          {comic.longDescription}
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {comic.tags.map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1 rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-700">
              <i className="ri-price-tag-3-line text-foreground-400" aria-hidden="true" />
              {tag}
            </span>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <button type="button" onClick={onStartReading} className="btn-primary !px-7 !py-3 text-base">
            <i className="ri-play-fill text-lg" aria-hidden="true" />
            开始阅读
          </button>
          <button
            type="button"
            onClick={() => setLiked((v) => !v)}
            className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border px-6 py-3 text-sm font-medium transition-colors cursor-pointer ${
              liked
                ? "border-primary-500 bg-primary-50 text-primary-600"
                : "border-foreground-950/15 bg-background-50 text-foreground-900 hover:border-primary-500 hover:text-primary-600"
            }`}
          >
            <i className={liked ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true" />
            {liked ? `已喜欢 ${formatCount(comic.likes + 1)}` : `喜欢 ${formatCount(comic.likes)}`}
          </button>
          <button
            type="button"
            onClick={() => setCollected((v) => !v)}
            className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border px-6 py-3 text-sm font-medium transition-colors cursor-pointer ${
              collected
                ? "border-accent-500 bg-accent-50 text-accent-700"
                : "border-foreground-950/15 bg-background-50 text-foreground-900 hover:border-accent-500 hover:text-accent-700"
            }`}
          >
            <i className={collected ? "ri-bookmark-fill" : "ri-bookmark-line"} aria-hidden="true" />
            {collected ? "已收藏" : "加入收藏"}
          </button>
        </div>
      </div>
    </div>
  );
}